// ./src/components/Upload.jsx
import React, { useState, useRef } from "react";
import {
  CloudUpload,
  File,
  X,
  Video,
  Music,
  Sparkles,
  CheckCircle,
  ArrowUpCircle,
  Zap,
} from "lucide-react";
import apiService from "../services/apiService";

const Upload = ({
  onUploadStart,
  onTranscriptReceived,
  onError,
  isProcessing,
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);

  // Geprueft wird ueber die Dateiendung, nicht ueber den MIME-Typ.
  //
  // Vorher stand hier eine Liste wie ["audio/mp3", "audio/m4a",
  // "video/mov", "video/avi"] und dazu ein exakter Vergleich. Das konnte
  // nicht funktionieren: Browser melden fuer diese Formate andere Typen -
  // MP3 kommt als "audio/mpeg", M4A als "audio/x-m4a", MOV als
  // "video/quicktime", AVI als "video/x-msvideo". Vier der acht angeblich
  // unterstuetzten Formate wurden also abgewiesen, und weil dieselbe Liste
  // im accept-Attribut steckte, liessen sie sich nicht einmal auswaehlen.
  //
  // Dateiendungen sind hier verlaesslicher: Sie haengen nicht davon ab,
  // welcher Browser die Datei uebergibt.
  //
  // Der Umfang folgt dem, was die Groq-Transkription tatsaechlich
  // verarbeitet - ogg, opus und flac waren bisher gar nicht angeboten.
  const erlaubteEndungen = [
    ".mp3", ".mp4", ".m4a", ".wav", ".webm",
    ".ogg", ".opus", ".flac", ".mpeg", ".mpga",
    ".mov", ".avi",
  ];

  // 25 MB ist die Obergrenze der Groq-Audio-Schnittstelle. Der Wert war
  // richtig, nur Kommentar und Fehlermeldung sprachen von 500 MB - wer
  // eine 80-MB-Datei waehlte, las "darf maximal 500MB gross sein".
  const maxFileSize = 25 * 1024 * 1024; // 25 MB

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    setError(null);

    // Validate file type
    const endung = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();

    if (!erlaubteEndungen.includes(endung)) {
      setError(
        "Dateityp nicht unterstützt. Möglich sind: " +
          erlaubteEndungen.join(", ")
      );
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      setError(
        "Datei zu groß (" +
          formatFileSize(file.size) +
          "). Maximal 25 MB sind möglich."
      );
      return;
    }

    setSelectedFile(file);
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // Handle upload and transcription
  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setError(null);
      onUploadStart();

      console.log("Uploading file to N8N:", selectedFile.name);

      const result = await apiService.uploadForTranscription(selectedFile);

      if (result.success && result.transcript) {
        console.log("Transcription received:", result.transcript);
        onTranscriptReceived(result.transcript);
      } else {
        throw new Error("Keine Transkription erhalten");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setError(err.error || err.message || "Upload fehlgeschlagen");
      onError();
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get file type icon
  const getFileIcon = (type) => {
    if (type.startsWith("video/")) return Video;
    if (type.startsWith("audio/")) return Music;
    return File;
  };

  return (
    <div className="card-premium p-8 animate-fadeInUp">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
          <CloudUpload className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gradient">Datei hochladen</h2>
        <div className="flex items-center space-x-1 px-3 py-1 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-full">
          <Sparkles className="w-3 h-3 text-emerald-600" />
          <span className="text-xs font-medium text-emerald-700">AI Ready</span>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl animate-slideInRight">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
              <X className="w-4 h-4 text-red-600" />
            </div>
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      {!selectedFile ? (
        <div
          className={`upload-area cursor-pointer group ${
            dragActive ? "dragover" : ""
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="relative">
            <CloudUpload
              className={`w-16 h-16 mx-auto mb-6 transition-all duration-500 ${
                dragActive
                  ? "text-primary-600 animate-float"
                  : "text-neutral-400 group-hover:text-primary-500"
              }`}
            />

            {/* Moderne Upload Animation */}
            {dragActive && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 border-4 border-primary-300 rounded-full animate-pulse-glow"></div>
              </div>
            )}
          </div>

          <div className="text-center space-y-4">
            <h3 className="text-xl font-bold text-neutral-800 group-hover:text-gradient transition-all duration-300">
              Datei hier ablegen oder klicken
            </h3>

            <div className="space-y-2">
              <p className="text-neutral-600">
                Video- oder Audio-Dateien bis 25MB
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {["MP4", "MOV", "AVI", "WebM", "MP3", "WAV", "M4A"].map(
                  (format) => (
                    <span
                      key={format}
                      className="px-3 py-1 bg-gradient-to-r from-neutral-100 to-neutral-200 text-neutral-600 rounded-full text-sm font-medium"
                    >
                      {format}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-neutral-200">
              <div className="text-center group/feature">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover/feature:scale-110 transition-transform duration-300">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-xs text-neutral-600 font-medium">
                  Blitzschnell
                </p>
              </div>

              <div className="text-center group/feature">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover/feature:scale-110 transition-transform duration-300">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-xs text-neutral-600 font-medium">
                  AI-Powered
                </p>
              </div>

              <div className="text-center group/feature">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover/feature:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-5 h-5 text-cyan-600" />
                </div>
                <p className="text-xs text-neutral-600 font-medium">Sicher</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Selected File Display */}
          <div className="card-modern p-6 hover-lift">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center">
                  {React.createElement(getFileIcon(selectedFile.type), {
                    className: "w-7 h-7 text-primary-600",
                  })}
                </div>
                <div>
                  <h4 className="font-bold text-neutral-800 text-lg">
                    {selectedFile.name}
                  </h4>
                  <p className="text-neutral-500 font-medium">
                    {formatFileSize(selectedFile.size)}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-emerald-600 font-medium">
                      Bereit für Analyse
                    </span>
                  </div>
                </div>
              </div>
              {!isProcessing && (
                <button
                  onClick={() => setSelectedFile(null)}
                  className="p-3 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all duration-300 hover:scale-110"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={isProcessing}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
          >
            <div className="flex items-center justify-center space-x-3 relative z-10">
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="font-bold">Wird transkribiert...</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                </>
              ) : (
                <>
                  <ArrowUpCircle className="w-5 h-5 group-hover:animate-float" />
                  <span className="font-bold">Transkription starten</span>
                  <Sparkles className="w-4 h-4 group-hover:animate-pulse" />
                </>
              )}
            </div>
          </button>

          {/* Processing Info */}
          {isProcessing && (
            <div className="processing-pulse bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6 animate-fadeInUp">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center animate-pulse-glow">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-orange-900 text-lg">
                    KI analysiert dein Meeting
                  </h4>
                  <p className="text-orange-700">
                    Das dauert nur wenige Minuten. Wir extrahieren automatisch
                    alle wichtigen Informationen.
                  </p>
                </div>
              </div>

              {/* Progress Steps */}
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="w-8 h-8 bg-orange-200 rounded-xl flex items-center justify-center mx-auto mb-2 animate-pulse">
                    <span className="text-orange-600 font-bold text-sm">1</span>
                  </div>
                  <p className="text-xs text-orange-600 font-medium">
                    Transkription
                  </p>
                </div>
                <div className="text-center opacity-60">
                  <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <span className="text-orange-500 font-bold text-sm">2</span>
                  </div>
                  <p className="text-xs text-orange-500 font-medium">Analyse</p>
                </div>
                <div className="text-center opacity-30">
                  <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <span className="text-orange-400 font-bold text-sm">3</span>
                  </div>
                  <p className="text-xs text-orange-400 font-medium">Fertig</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Endungen UND grobe Typen: Die Endungen sorgen dafuer, dass der
          Auswahldialog die richtigen Dateien anbietet, audio/* und video/*
          fangen Formate ab, die ein Geraet anders benennt. Vorher stand
          hier dieselbe fehlerhafte MIME-Liste wie in der Pruefung - eine
          M4A-Datei liess sich deshalb nicht einmal auswaehlen. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={erlaubteEndungen.join(",") + ",audio/*,video/*"}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default Upload;
